import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { projects as staticProjects } from "@/data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Project() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    const fetchOne = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/projects/${slug}`);
        // Map backend field to the shape used by static list (hero_image → hero)
        if (!cancelled) {
          setProject({
            ...data,
            hero: data.hero_image,
            thumb: data.hero_image,
          });
        }
      } catch (e) {
        const fallback = staticProjects.find((p) => p.slug === slug);
        if (!cancelled) setProject(fallback || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream text-ink grid place-items-center" data-testid="project-loading">
        <p className="label text-ink2">Loading…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-cream text-ink grid place-items-center" data-testid="project-not-found">
        <div className="text-center">
          <p className="chapter-num">404 · Not found</p>
          <h1 className="mt-4 font-display text-5xl">Project not found</h1>
          <button
            onClick={() => navigate("/")}
            className="mt-8 px-6 py-3 border border-ink text-ink label hover:bg-ink hover:text-cream"
            data-testid="project-back-home"
          >
            Back to studio
          </button>
        </div>
      </div>
    );
  }

  const idx = staticProjects.findIndex((p) => p.slug === project.slug);
  const nextProject =
    idx >= 0 ? staticProjects[(idx + 1) % staticProjects.length] : null;

  return (
    <main className="bg-cream text-ink no-x pt-24" data-testid="project-page">
      {/* Back */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 mb-10 md:mb-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 label text-ink2 editorial-link"
          data-testid="project-back"
          data-cursor="hover"
        >
          <ArrowLeft size={14} /> Back to work
        </Link>
      </div>

      {/* Title */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
        <div className="col-span-12 md:col-span-8">
          <p className="chapter-num">
            {project.category} · {project.year}
          </p>
          <h1 className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tight text-ink">
            {project.title}.
          </h1>
          <p className="mt-6 font-sans font-light text-xl md:text-2xl text-ink2 max-w-[52ch]">
            {project.subtitle}
          </p>
        </div>
        <div className="col-span-12 md:col-span-3 md:col-start-10">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-6 border-t border-ink/15 pt-6">
            <div>
              <p className="chapter-num">Location</p>
              <p className="mt-1 font-sans text-sm text-ink">{project.location}</p>
            </div>
            <div>
              <p className="chapter-num">Role</p>
              <p className="mt-1 font-sans text-sm text-ink">{project.role}</p>
            </div>
            <div>
              <p className="chapter-num">Materials</p>
              <ul className="mt-1 font-sans text-sm text-ink space-y-1">
                {project.materials.map((m) => (
                  <li key={m}>· {m}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.1 }}
        className="img-frame aspect-[16/10] mx-auto max-w-[1600px] px-6 md:px-10"
        style={{ padding: 0 }}
      >
        <div className="img-frame aspect-[16/10]">
          <img
            src={project.hero || project.hero_image}
            alt={project.title}
            data-testid="project-hero-image"
          />
        </div>
      </motion.div>

      {/* Description block */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-2 chapter-num">
          The Brief
        </div>
        <p className="col-span-12 md:col-span-8 md:col-start-4 font-display text-3xl md:text-4xl leading-[1.25] text-ink">
          {project.description}
        </p>
      </div>

      {/* Gallery */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-12 gap-6 md:gap-10">
        {project.gallery.map((src, i) => {
          const shapes = [
            "col-span-12 aspect-[16/9]",
            "col-span-12 md:col-span-6 aspect-[4/5]",
            "col-span-12 md:col-span-6 aspect-[4/5]",
            "col-span-12 aspect-[21/9]",
            "col-span-12 md:col-span-8 aspect-[16/10]",
            "col-span-12 md:col-span-4 aspect-[4/5]",
          ];
          return (
            <motion.div
              key={src + i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.9, delay: i * 0.05 }}
              className={`img-frame ${shapes[i % shapes.length]}`}
              data-testid={`project-gallery-${i}`}
            >
              <img src={src} alt={`${project.title} ${i + 1}`} loading="lazy" />
            </motion.div>
          );
        })}
      </div>

      {/* Next project, massive link */}
      {nextProject && (
        <Link
          to={`/work/${nextProject.slug}`}
          className="block border-t border-ink/15 relative group overflow-hidden"
          data-testid="project-next"
          data-cursor="view"
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
            <img
              src={nextProject.thumb}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-ink/60" />
          </div>
          <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32 flex items-center justify-between">
            <div>
              <p className="label text-ink2 group-hover:text-cream/70 transition-colors">
                Next Project · 0{((idx + 1) % staticProjects.length) + 1}
              </p>
              <h2 className="mt-4 font-display text-6xl md:text-8xl text-ink group-hover:text-cream transition-colors leading-[0.95]">
                {nextProject.title}.
              </h2>
            </div>
            <ArrowUpRight
              size={64}
              className="text-ink group-hover:text-cream transition-transform group-hover:rotate-45"
            />
          </div>
        </Link>
      )}
    </main>
  );
}
